// src/pages/CompleteSubscription.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getChildren, isAdultAccount } from '../utils/childrenHelper';
import { resolveDiscount } from '../utils/discountPricing';
import { TRIAL_SIGNUP_ENABLED } from '../config/featureFlags';
import { Crown, Sparkles, Check, Shield, Star, Tag, CheckCircle, XCircle, Loader2, RotateCcw, AlertTriangle, Users, User } from 'lucide-react';
import { stripePromise, validateDiscountCode } from '../config/stripe';
import { COURSE_PACKAGES } from '../config/coursePackages';
import { cleanupExpiredAccount } from '../config/stripe';

function CompleteSubscription() {
  const { user, authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentCanceled = searchParams.get('canceled') === 'true';
  
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  
  // Save payment method checkbox
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);
  
  // Coupon code states
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [validationTimeout, setValidationTimeout] = useState(null);

  // Start over states
  const [isResetting, setIsResetting] = useState(false);
  const [showConfirmReset, setShowConfirmReset] = useState(false);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (authLoading) return;
      
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProfile(userData);
          
          // Set save payment method from user data or localStorage
          const savedUserData = JSON.parse(localStorage.getItem("user") || '{}');
          setSavePaymentMethod(userData.payment?.savePaymentMethod || savedUserData.savePaymentMethod || false);

          // Check if ANY child has an active course
          const allChildren = getChildren(userData);
          const hasActiveChild = allChildren.some(child =>
            child.course?.status === 'active'
          );

          if (hasActiveChild) {
            console.log('✅ User has active child(ren), redirecting to home');
            navigate('/', { replace: true });
            return;
          }

          // Returning customer: any child with a completed or expired course
          const hasEndedChild = allChildren.some(child =>
            child.course?.status === 'completed' || child.course?.status === 'expired'
          );

          if (hasEndedChild) {
            navigate('/course-renewal', { replace: true });
            return;
          }

          // Legacy check for old structure
          if (userData.course?.status === 'active') {
            navigate('/', { replace: true });
            return;
          }

          if (userData.course?.status === 'completed' || userData.course?.status === 'expired') {
            navigate('/course-renewal', { replace: true });
            return;
          }

          // Trial signup with no course yet — belongs on /trial-status, not in
          // the payment funnel. Same condition as the ProtectedRoute trial
          // guard; placed after the active / ended checks so a converted trial
          // or a returning customer is never caught here.
          if (
            TRIAL_SIGNUP_ENABLED &&
            userData.signupSource === 'trial' &&
            !allChildren.some(child => !!child.course)
          ) {
            navigate('/trial-status', { replace: true });
            return;
          }
        } else {
          // User document doesn't exist, redirect to select-plan
          navigate('/select-plan', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
      
      setIsLoading(false);
    };

    loadUserProfile();
  }, [user, authLoading, navigate]);

  // Debounced coupon validation
  const validateCouponDebounced = useCallback(async (code) => {
    if (!code.trim()) {
      setDiscountValidation(null);
      setIsValidatingCoupon(false);
      return;
    }

    setIsValidatingCoupon(true);
    
    try {
      const children = getChildren(userProfile);
      // Validate the code and get its coupon back only — the displayed discount
      // is priced by resolveDiscount against the real basket in calculatePricing,
      // so this call's own discountAmount/finalAmount are unused. The real basket
      // subtotal (cents) IS sent so the server can check a code's minimum_amount
      // against a mixed-package basket. Package + quantity 1 are just a valid shape.
      const packageId = children[0]?.course?.packageId || 'course_6week_group';
      const subtotalCents = Math.round(
        children.reduce((sum, c) => {
          const pkg = COURSE_PACKAGES[c.course?.packageId];
          return sum + (pkg ? pkg.price : 0);
        }, 0) * 100,
      );
      const validation = await validateDiscountCode(code, packageId, 1, subtotalCents);
      setDiscountValidation(validation);
    } catch (error) {
      setDiscountValidation({ 
        valid: false, 
        error: error.message || 'Invalid discount code'
      });
    } finally {
      setIsValidatingCoupon(false);
    }
  }, [userProfile]);

  // Handle discount code changes with debouncing
  const handleDiscountCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setDiscountCode(code);
    
    // Clear existing timeout
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }
    
    // Set new timeout for validation
    const newTimeout = setTimeout(() => {
      validateCouponDebounced(code);
    }, 500);
    
    setValidationTimeout(newTimeout);
  };

  // Calculate pricing with discount
  const calculatePricing = () => {
    const children = getChildren(userProfile);
    
    if (children.length === 0) return null;
    
    // Calculate base price from all children's individual packages
    const basePrice = children.reduce((total, child) => {
      const pkg = COURSE_PACKAGES[child.course?.packageId];
      return total + (pkg ? pkg.price : 0);
    }, 0);
    
    // Discount priced against the REAL (possibly mixed-package) basket — see
    // src/utils/discountPricing.js. The old firstPackage×count shortcut showed a
    // figure that didn't match what Stripe charged on a mixed basket.
    // DISPLAY ONLY: handlePayment / createCheckoutSession are untouched.
    const d = resolveDiscount(
      discountValidation?.valid ? discountValidation.coupon : null,
      basePrice,
    );
    const discountApplies =
      !!discountValidation?.valid && !d.wrongCurrency && d.amount > 0;
    const discountAmount = discountApplies ? d.amount : 0;

    return {
      basePrice,
      discountAmount,
      finalPrice: Math.max(0, basePrice - discountAmount),
      hasDiscount: discountApplies,
      discountApplies,
      discountWrongCurrency: d.wrongCurrency,
    };
  };

  const handlePayment = async () => {
    setIsProcessingPayment(true);
    setPaymentError(null);
    
    try {
      const stripe = await stripePromise;
      
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Get children data
      const children = getChildren(userProfile);
      
      if (children.length === 0) {
        throw new Error('No children found. Please start over.');
      }

      console.log('🎯 Processing initial signup payment for children:', children.map(c => c.name));

      // Call unified createCheckoutSession with children array (new format)
      const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL || 
        'https://us-central1-arabic-ai-kids.cloudfunctions.net';
      
      const response = await fetch(`${FUNCTIONS_BASE_URL}/createCheckoutSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
          children: children.map(child => ({
            name: child.name,
            dateOfBirth: child.dateOfBirth,
            packageId: child.course?.packageId
          })),
          userEmail: user.email,
          savePaymentMethod: savePaymentMethod,
          discountCode: discountCode || null,
          enrollmentType: 'initial_signup',
          isRenewal: false,
          successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/complete-subscription?canceled=true`
        }),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }

        const e = new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
        // Carry the discount marker so the catch can route it to the coupon field.
        if (errorData.discountError) e.discountError = true;
        throw e;
      }

      const session = await response.json();

      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({
        sessionId: session.sessionId,
      });

      if (error) {
        throw error;
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      // A promotion-code restriction failure (Stripe refused the code) belongs
      // next to the coupon field, not as a page-level payment error.
      if (error && error.discountError) {
        setDiscountValidation({ valid: false, error: error.message });
        setPaymentError(null);
      } else {
        setPaymentError(error.message || 'Payment failed. Please try again.');
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleChangePlan = () => {
    navigate('/select-plan');
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  // Start over functionality
  const handleStartOver = () => {
    setShowConfirmReset(true);
  };

  const confirmStartOver = async () => {
    setIsResetting(true);
    setShowConfirmReset(false);
    setPaymentError(null);

    try {
      // Trigger account cleanup (deletes both auth and firestore)
      await cleanupExpiredAccount(user.uid);
      
      // Clear localStorage
      localStorage.removeItem('user');
      
      // Navigate will happen automatically when auth state changes
      // The AuthContext will detect the user is signed out
      window.location.href = '/select-plan'; // Use window.location instead of navigate
    } catch (error) {
      console.error('Error during reset:', error);
      setPaymentError('Failed to reset account. Please contact support.');
      setIsResetting(false);
    }
  };

  const cancelStartOver = () => {
    setShowConfirmReset(false);
  };

  // Show confirm reset dialog
  if (showConfirmReset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl max-w-md">
          <div className="text-center mb-6">
            <AlertTriangle className="text-yellow-500 mx-auto mb-4" size={48} />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Start Over?</h2>
            <p className="text-gray-600">
              This will delete your current account and you'll need to create a new one. This action cannot be undone.
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={cancelStartOver}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmStartOver}
              disabled={isResetting}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-xl transition-colors disabled:opacity-50"
            >
              {isResetting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Resetting...
                </div>
              ) : (
                'Yes, Start Over'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-purple-600 text-xl font-bold">Loading your account...</div>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md">
          <div className="text-red-500 text-xl font-bold mb-4">Account Not Found</div>
          <p className="text-gray-600 mb-6">We couldn't find your account information. Please sign up again.</p>
          <button
            onClick={handleBackToLogin}
            className="w-full bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Get children data
  const children = getChildren(userProfile);
  
  if (children.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md">
          <div className="text-red-500 text-xl font-bold mb-4">Configuration Error</div>
          <p className="text-gray-700 mb-6">No children found in your account.</p>
          <button 
            onClick={handleChangePlan}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  const pricing = calculatePricing();
  const packages = COURSE_PACKAGES;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
            <Sparkles className="text-yellow-400" size={24} />
            <span className="text-white font-bold text-lg">Complete Your Journey</span>
            <Sparkles className="text-yellow-400" size={24} />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 drop-shadow-2xl">
            Welcome, {userProfile.firstName}! 👋
          </h1>
          {paymentCanceled ? (
            <p className="text-xl text-red-200 max-w-2xl mx-auto">
              Payment was canceled. You can try again when you're ready.
            </p>
          ) : (
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              Complete your payment to unlock your premium Arabic learning experience!
            </p>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left side - Order Summary */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <Star className="text-yellow-500" size={24} />
              <h2 className="text-2xl font-bold text-gray-800">Order Summary</h2>
            </div>

            {/* Per-Child Breakdown */}
            <div className="space-y-3 mb-6">
              {children.map((child, index) => {
                const pkg = packages[child.course?.packageId];
                if (!pkg) return null;
                
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      {COURSE_PACKAGES[child.course?.packageId]?.type === 'group' ? (
                        <Users className="text-purple-600" size={20} />
                      ) : (
                        <User className="text-purple-600" size={20} />
                      )}
                      <div>
                        <div className="font-semibold text-gray-800">{child.name}</div>
                        <div className="text-sm text-gray-600">
                          {COURSE_PACKAGES[child.course?.packageId]?.shortLabel || 'Course'} • {COURSE_PACKAGES[child.course?.packageId]?.duration?.weeks || 6} weeks
                        </div>
                      </div>
                    </div>
                    <div className="text-lg font-bold text-purple-600">
                      ${pkg.price}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Pricing */}
            {pricing?.hasDiscount ? (
              <div className="border-t pt-4">
                <div className="flex justify-between items-center text-sm text-gray-600 mb-1">
                  <span>Subtotal</span>
                  <span className="line-through">${pricing.basePrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-green-600 mb-2">
                  <span>Discount</span>
                  <span>-${pricing.discountAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Total</span>
                  <span className="text-2xl text-purple-600">${pricing.finalPrice.toFixed(2)} CAD</span>
                </div>
              </div>
            ) : (
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">Total</span>
                  <span className="font-bold text-2xl text-purple-600">${pricing.basePrice.toFixed(2)} CAD</span>
                </div>
              </div>
            )}

            {/* Account Information */}
            <div className="bg-gray-50 rounded-xl p-4 mt-6">
              <h4 className="font-bold text-gray-800 mb-2">Account Information</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <div>Name: {userProfile.firstName} {userProfile.lastName}</div>
                <div>Email: {user.email}</div>
                {!isAdultAccount(userProfile) && (
                  <div>Children: {children.length}</div>
                )}
              </div>
            </div>
          </div>

          {/* Right side - Payment Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Crown size={16} />
                Complete Payment
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Ready to Start Learning?</h2>
              <p className="text-gray-600">Secure payment powered by Stripe</p>
            </div>

            {/* Payment Canceled Notice */}
            {paymentCanceled && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="text-yellow-500 text-2xl">⚠️</div>
                  <div>
                    <div className="font-semibold text-yellow-800">Payment Canceled</div>
                    <div className="text-yellow-700 text-sm">You can try again when you're ready.</div>
                  </div>
                </div>
              </div>
            )}

            {/* Discount Code Section */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Discount Code (Optional)
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={discountCode}
                  onChange={handleDiscountCodeChange}
                  placeholder="Enter discount code"
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {isValidatingCoupon && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="animate-spin text-purple-600" size={18} />
                  </div>
                )}
              </div>
              
              {discountValidation && (
                <div className={`mt-2 flex items-center gap-2 text-sm ${pricing?.discountApplies ? 'text-green-600' : 'text-red-600'}`}>
                  {pricing?.discountApplies ? <CheckCircle size={16} /> : <XCircle size={16} />}
                  {!discountValidation.valid
                    ? discountValidation.error
                    : pricing?.discountWrongCurrency
                      ? "This code can't be applied to your order."
                      : pricing?.discountApplies
                        ? `Valid! $${(pricing.discountAmount || 0).toFixed(2)} discount applied`
                        : 'This code has no effect on your order.'}
                </div>
              )}
            </div>

            {/* Save Payment Method Checkbox */}
            <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="savePayment"
                  checked={savePaymentMethod}
                  onChange={(e) => setSavePaymentMethod(e.target.checked)}
                  className="mt-1"
                />
                <label htmlFor="savePayment" className="text-sm text-gray-700">
                  <div className="font-semibold mb-1">💳 Save payment method for easy renewal</div>
                  <div className="text-xs text-gray-600">
                    Securely stored by Stripe. You can remove it anytime from your account settings.
                  </div>
                </label>
              </div>
            </div>

            {/* Error Message */}
            {paymentError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center gap-2 text-red-700">
                  <XCircle size={20} />
                  <div className="text-sm font-medium">{paymentError}</div>
                </div>
              </div>
            )}

            {/* Payment Button */}
            <button
              onClick={handlePayment}
              disabled={isProcessingPayment}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:transform-none shadow-lg hover:shadow-xl mb-4"
            >
              {isProcessingPayment ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Shield size={20} />
                  Pay ${pricing?.finalPrice.toFixed(2)} CAD
                </div>
              )}
            </button>

            {/* Start Over Button */}
            <button
              onClick={handleStartOver}
              disabled={isProcessingPayment}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              <RotateCcw size={18} />
              Start Over
            </button>

            {/* Trust Badges */}
            <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Shield size={14} className="text-green-500" />
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center gap-1">
                <Check size={14} className="text-green-500" />
                <span>SSL Encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompleteSubscription;